import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loginLogSchema } from '@competa/shared';
import { apiRequest } from '../../api/client';
import { TextField } from '../../components/TextField';
import { errorMessage } from '../../i18n/errors';
import styles from './Admin.module.css';

/**
 * Formatiert eine Dauer in Sekunden kompakt (s, min, h).
 * @param seconds Dauer in Sekunden.
 * @returns Text wie `45s`, `12min` oder `2h 5min`.
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}min`;
}

/** Login-Protokoll: je Benutzer die neuesten Anmeldungen, filterbar nach Namen. */
export function LogPage() {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState('');
  const [limit, setLimit] = useState(3);

  const log = useQuery({
    queryKey: ['admin', 'sessions', filter, limit],
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (filter.trim()) params.set('username', filter.trim());
      return apiRequest('GET', `/admin/sessions?${params}`, { schema: loginLogSchema });
    },
  });

  return (
    <div>
      <h2>{t('admin.log.title')}</h2>
      <div className={styles.toolbar}>
        <TextField
          label={t('admin.log.filterUser')}
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <TextField
          label={t('admin.log.limit')}
          type="number"
          min={1}
          max={100}
          value={limit}
          onChange={(event) =>
            setLimit(Math.min(100, Math.max(1, Number(event.target.value) || 1)))
          }
        />
      </div>
      {log.isPending && <p role="status">{t('common.loading')}</p>}
      {log.isError && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, log.error)}
        </p>
      )}
      {log.data && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.log.user')}</th>
                <th>{t('admin.log.loginAt')}</th>
                <th>{t('admin.log.logoutAt')}</th>
                <th>{t('admin.log.duration')}</th>
                <th>{t('admin.log.ip')}</th>
              </tr>
            </thead>
            <tbody>
              {log.data.entries.length === 0 && (
                <tr>
                  <td colSpan={5}>{t('admin.log.empty')}</td>
                </tr>
              )}
              {log.data.entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <strong>{entry.username}</strong>
                  </td>
                  <td>{new Date(entry.loginAt).toLocaleString(i18n.language)}</td>
                  <td>
                    {entry.logoutAt ? new Date(entry.logoutAt).toLocaleString(i18n.language) : '–'}
                  </td>
                  <td>
                    {entry.durationSeconds === null
                      ? t('admin.log.open')
                      : formatDuration(entry.durationSeconds)}
                  </td>
                  <td>{entry.ip ?? '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
